import moment from 'moment';

import { setFacebookUser } from '../analytics/facebook';
import { setGoogleUserId } from '../analytics/firebase';
import { getClientData, setClientData } from '../util/clientData';
import { logError } from '../util/log';
import { isCordova } from '../util/mobile/deviceDetection';
import { oneSignalMaybeGrantConsent, oneSignalRemoveTags } from '../util/mobile/oneSignal';
import { getStoryblokClient } from '../util/storyblok';
import { authInfo } from './auth.duck';
import { notifyMailContactEvent } from './mailContact.duck';
import { updateUserProfile } from './user.duck';

// ================ Action types ================ //

export const STARTUP_REQUEST = 'app/startUp/STARTUP_REQUEST';
export const STARTUP_SUCCESS = 'app/startUp/STARTUP_SUCCESS';

export const STARTUP_APP_USAGE_REQUEST = 'app/startUp/STARTUP_APP_USAGE_REQUEST';
export const STARTUP_APP_USAGE_SUCCESS = 'app/startUp/STARTUP_APP_USAGE_SUCCESS';

export const STARTUP_POLICIES_REQUEST = 'app/startUp/STARTUP_POLICIES_REQUEST';
export const STARTUP_POLICIES_SUCCESS = 'app/startUp/STARTUP_POLICIES_SUCCESS';

export const STARTUP_LOCATION_REQUEST = 'app/startUp/STARTUP_LOCATION_REQUEST';
export const STARTUP_LOCATION_SUCCESS = 'app/startUp/STARTUP_LOCATION_SUCCESS';

export const SET_USER_ID_REQUEST = 'app/startUp/SET_USER_ID_REQUEST';

export const SET_ONE_SIGNAL_USER_ID_REQUEST = 'app/startUp/SET_ONE_SIGNAL_USER_ID_REQUEST';

export const SET_ONLINE_STATUS = 'app/startUp/SET_ONLINE_STATUS';

// ================ Reducer ================ //

/** @type {import('../../types/vansite').VanSite.State.StartUp} */
const initialState = {
  onlineStatus: typeof window === 'undefined' ? true : navigator.onLine,
  appUsageAccepted: false,
  appUsageModalOpen: false,
  policiesAccepted: false,
  policiesChangedModalOpen: false,
  locationAsked: false,
  startUpInProgress: false,
  userIdSet: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case STARTUP_REQUEST:
      return { ...state, startUpInProgress: true };
    case STARTUP_SUCCESS:
      return { ...state, startUpInProgress: false };

    case STARTUP_APP_USAGE_REQUEST:
      return { ...state, appUsageModalOpen: true };
    case STARTUP_APP_USAGE_SUCCESS:
      return { ...state, appUsageModalOpen: false, appUsageAccepted: true };

    case STARTUP_POLICIES_REQUEST:
      return { ...state, policiesChangedModalOpen: true };
    case STARTUP_POLICIES_SUCCESS:
      return { ...state, policiesChangedModalOpen: false, policiesAccepted: true };

    case STARTUP_LOCATION_REQUEST:
      return { ...state };
    case STARTUP_LOCATION_SUCCESS:
      return { ...state, locationAsked: true };

    case SET_USER_ID_REQUEST:
      return { ...state, userIdSet: true };

    case SET_ONE_SIGNAL_USER_ID_REQUEST:
      return { ...state, userIdSet: true };

    case SET_ONLINE_STATUS:
      return { ...state, onlineStatus: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const startupRequest = () => ({ type: STARTUP_REQUEST });
export const startupSuccess = () => ({ type: STARTUP_SUCCESS });
export const startupAppUsageRequest = () => ({ type: STARTUP_APP_USAGE_REQUEST });
export const startupAppUsageSuccess = () => ({ type: STARTUP_APP_USAGE_SUCCESS });
export const startupPoliciesRequest = () => ({ type: STARTUP_POLICIES_REQUEST });
export const startupPoliciesSuccess = () => ({ type: STARTUP_POLICIES_SUCCESS });
export const startupLocationRequest = () => ({ type: STARTUP_LOCATION_REQUEST });
export const startupLocationSuccess = () => ({ type: STARTUP_LOCATION_SUCCESS });
export const setUserIdRequest = () => ({ type: SET_USER_ID_REQUEST });

export const setOnlineStatus = (online) => ({ type: SET_ONLINE_STATUS, payload: online });

// ================ Thunks ================ //

export const acceptAppUsage = () => async (dispatch) => {
  setClientData('vansite.appUsageAccepted', true, moment().add(1, 'year').toISOString());
  // OneSignal consent is granted only once the cookie-consent startup screen
  // is also answered — otherwise its in-app messages (push prompt, campaigns)
  // pop up on top of that screen. The registered:false tag seeding moved into
  // the grant path for the same reason.
  oneSignalMaybeGrantConsent();
  dispatch(startupAppUsageSuccess());
  dispatch(startUpVanSite());
};

export const acknowledgePolicesChange = () => async (dispatch) => {
  dispatch(
    updateUserProfile({
      privateData: {
        policyAcknowledgment: null,
        lastPolicyAcknowledged: moment().toISOString(),
      },
    }),
  );
  dispatch(startupPoliciesSuccess());
  dispatch(startUpVanSite());
};

const setTackingUserId = (currentUser) => (dispatch) => {
  setGoogleUserId(currentUser.id.uuid);
  setFacebookUser(currentUser);
  dispatch(setUserIdRequest());
};

const initAppUsage = async (dispatch) => {
  const hasAppUsageAcceptedBefore = getClientData('vansite.appUsageAccepted');
  if (!hasAppUsageAcceptedBefore) {
    dispatch(startupAppUsageRequest());
  } else {
    dispatch(startupAppUsageSuccess());
    dispatch(startUpVanSite());
  }
};

const initLocation = async (dispatch) => {
  dispatch(startupLocationRequest());
  // navigator.geolocation is undefined in privacy-restricted browsers (Firefox
  // resistFingerprinting, some webviews). Skip silently — startup should not
  // hang waiting for a position that will never arrive.
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    dispatch(startupLocationSuccess());
    return;
  }
  const promise = new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        dispatch(startupLocationSuccess());
        resolve(position);
      },
      (error) => {
        dispatch(startupLocationSuccess());
        resolve(error);
      },
    );
  });
  await promise;
};

const initPolicies = async (dispatch, currentUser) => {
  const storyblokClient = getStoryblokClient();

  const response = await storyblokClient.get(`cdn/datasource_entries`, {
    datasource: 'dates',
  });
  const { data } = response;
  const { datasource_entries } = data;

  const lastPrivacyPolicyChangeDate = moment(
    datasource_entries.find((entry) => entry.name === 'privacyPolicyChangedAt').value,
    'DD.MM.YYYY',
  );
  const lastTermsOfServiceChangeDate = moment(
    datasource_entries.find((entry) => entry.name === 'termsOfServiceChangedAt').value,
    'DD.MM.YYYY',
  );

  const lastPolicyAcknowledged = currentUser?.attributes?.profile.privateData?.lastPolicyAcknowledged;
  const lastPolicyAcknowledgedMoment = lastPolicyAcknowledged
    ? moment(lastPolicyAcknowledged)
    : null;

  const shouldShowPoliciesRequest = (lastCheckedDate) =>
    lastCheckedDate.isAfter(lastPrivacyPolicyChangeDate) ||
    lastCheckedDate.isAfter(lastTermsOfServiceChangeDate);

  if (!(lastPolicyAcknowledgedMoment && shouldShowPoliciesRequest(lastPolicyAcknowledgedMoment))) {
    dispatch(startupPoliciesRequest()); // ends startup process, will be started again when user accepts tha policies via modal
  } else {
    dispatch(startupPoliciesSuccess());
  }
};

const onlineAndOfflineEventListeners = (dispatch) => {
  window.addEventListener('online', () => {
    dispatch(setOnlineStatus(true));
  });

  window.addEventListener('offline', () => {
    dispatch(setOnlineStatus(false));
  });
};

/**
 * @param {import('@vansite/ts-sharetribe-flex-sdk').CurrentUser} currentUser
 */
export const notifyAnnualPassExpiry = (currentUser) => async (dispatch) => {
  try {
    const annualPass = currentUser?.attributes?.profile.privateData.annualPass;
    if (!annualPass) return;
    const validUntilMoment = moment.utc(annualPass.validUntil);
    if (moment().utc().isAfter(validUntilMoment)) {
      // Der Server verifiziert das Ablaufdatum selbst - dieser Check spart
      // nur den Request im Normalfall.
      dispatch(notifyMailContactEvent('annual-pass-expired'));
    }
  } catch (e) {
    logError(e, 'mail-annual-pass-expiry', { currentUser });
  }
};

/**
 * @param {import('@vansite/ts-sharetribe-flex-sdk').CurrentUser} currentUser
 */
export const updateOneSignalTags = (currentUser) => async (dispatch) => {
  try {
    const annualPass = currentUser?.attributes?.profile.privateData.annualPass;
    if (annualPass) {
      const { validUntil } = annualPass;
      const today = moment().utc();
      const validUntilMoment = moment.utc(validUntil);
      if (today.isAfter(validUntilMoment)) {
        oneSignalRemoveTags(['annual_pass', 'annual_pass_until']);
      }
    }
  } catch (e) {
    logError(e, 'one-signal-update-user', { currentUser });
  }
};

export const initSetTackingUserId = () => async (dispatch, getState) => {
  const { currentUser } = getState().user;
  const { userIdSet } = getState().startUp;
  if (!userIdSet) {
    dispatch(setTackingUserId(currentUser));
    dispatch(setUserIdRequest());
  }
};

/**
 * @type {() => import('../../../types/vansite').AppThunkAction<Promise<void>>}
 */
export const startUpVanSite = () => async (dispatch, getState) => {
  dispatch(startupRequest());
  const { currentUser } = getState().user;
  const { appUsageAccepted, policiesAccepted, locationAsked, userIdSet } = getState().startUp;

  try {
    // check if user has accepted the app usage
    if (isCordova() && !appUsageAccepted) {
      await initAppUsage(dispatch);
      return;
    }

    const allChecks = [];
    if (!locationAsked) {
      allChecks.push(initLocation(dispatch));
    }

    // check if user has accepted the policies
    // restarts the the startup process if the user has not accepted the policies
    if (currentUser && !policiesAccepted) {
      allChecks.push(initPolicies(dispatch, currentUser));
    }

    // set user id tracking when user is logged in and policies are accepted
    // in app or in web
    if (currentUser && policiesAccepted && !userIdSet) {
      allChecks.push(dispatch(initSetTackingUserId()));
    }

    if (currentUser) {
      allChecks.push(dispatch(notifyAnnualPassExpiry(currentUser)));
      isCordova() && dispatch(updateOneSignalTags(currentUser));
    }

    Promise.all(allChecks);

    onlineAndOfflineEventListeners(dispatch);

    dispatch(startupSuccess());
  } catch (e) {
    logError(e, 'start-up', { currentUser });
  }
};
